import { useState } from 'react';

import { useFocusable } from '../focus';
import { IconNote } from '../icons';
import { colorFor } from '../styles/tokens';
import { Avatar } from './Avatar';
import { Tag } from './Chip';
import { CoverImage } from './CoverImage';
import { cx } from './cx';
import styles from './QueueRow.module.css';
import { Spinner } from './Spinner';

export type QueueRowState = 'queued' | 'playing' | 'played';

export interface QueueRowAction {
  key: string;
  label: string;
  primary?: boolean;
}

export interface QueueRowProps {
  title: string;
  artists: string;
  imageUrl?: string | null;
  color?: string;
  toneLabel: string;
  addedBy: { name: string; photo?: string | null };
  state?: QueueRowState;
  /** Chip shown on the playing row ("SONANDO"). */
  playingLabel?: string;
  /**
   * Inline actions shown while focused (Figma TV-05 68:556). Left/Right move between them,
   * Enter fires `onAction`. Up/Down still leave the row.
   */
  actions?: QueueRowAction[];
  onAction?: (key: string) => void;
  /** An action on this row is in flight (e.g. `play-now`): a spinner replaces the actions. */
  pending?: boolean;
  /**
   * Explicit D-pad wiring, consulted before the inline-action walk (UX-346). Return `false` to
   * consume the press. The drawer uses it to make its header button reachable from the top row.
   */
  onArrowPress?: (direction: string) => boolean;
  focusKey?: string;
  onFocus?: () => void;
  className?: string;
}

/**
 * One row of the queue drawer (Figma TV-05 68:526): 92 px tall, cover + title/artists, a tone
 * chip, the person who added it, and — while focused — the inline actions. No duration column
 * (docs/04 §7.3: `durationMs` is not shown anywhere on the TV).
 */
export function QueueRow({
  title,
  artists,
  imageUrl,
  color,
  toneLabel,
  addedBy,
  state = 'queued',
  playingLabel,
  actions = [],
  onAction,
  pending = false,
  onArrowPress,
  focusKey,
  onFocus,
  className,
}: QueueRowProps) {
  const [active, setActive] = useState(0);
  const {
    ref,
    focused,
    focusKey: key,
  } = useFocusable<HTMLDivElement>({
    focusKey,
    focusable: true,
    onEnterPress: () => {
      const action = actions[active] ?? actions[0];
      if (action) onAction?.(action.key);
    },
    onArrowPress: (direction) => {
      if (onArrowPress?.(direction) === false) return false;
      if (actions.length < 2) return true;
      if (direction === 'left') {
        setActive((i) => Math.max(0, i - 1));
        return false;
      }
      if (direction === 'right') {
        setActive((i) => Math.min(actions.length - 1, i + 1));
        return false;
      }
      return true;
    },
    onFocus: () => {
      setActive(0);
      onFocus?.();
    },
  });
  const playing = state === 'playing';
  return (
    <div
      ref={ref}
      role="listitem"
      data-focus-key={key}
      data-focused={focused || undefined}
      data-state={state}
      className={cx(styles.row, styles[state], focused && styles.focused, className)}
    >
      <div className={styles.cover} style={{ background: color ?? colorFor(title) }}>
        <CoverImage
          src={imageUrl}
          sizeHint={96}
          fallback={<IconNote size={34} className={styles.note} />}
        />
      </div>
      <div className={styles.info}>
        <div className={styles.title} data-testid="queue-row-title">
          {title}
        </div>
        {artists ? <div className={styles.artists}>{artists}</div> : null}
      </div>
      <div className={styles.spacer} />
      {playing && playingLabel ? (
        <Tag tone="dark" className={styles.tone} data-testid="queue-row-playing">
          {playingLabel}
        </Tag>
      ) : null}
      <Tag tone={playing ? 'dark' : 'cyan'} className={styles.tone}>
        {toneLabel}
      </Tag>
      <div className={styles.addedBy}>
        <Avatar name={addedBy.name} src={addedBy.photo} size={48} />
        <span className={styles.addedByName}>{addedBy.name}</span>
      </div>
      {pending ? (
        <span className={styles.pending} data-testid="row-pending">
          <Spinner size={40} />
        </span>
      ) : focused && actions.length ? (
        <div className={styles.actions}>
          {actions.map((action, i) => (
            <span
              key={action.key}
              data-testid={`row-action-${action.key}`}
              data-active={i === active || undefined}
              className={cx(styles.actionBtn, i === active && styles.actionActive)}
            >
              {action.label}
            </span>
          ))}
        </div>
      ) : null}
    </div>
  );
}
